import api from '../api';

export type BenefitType = 'cost_savings' | 'growth_capacity' | 'efficiency' | 'risk_control_automation';

export interface OpportunityBenefit {
  id: number;
  project_id: number;
  opportunity_id: number;
  skill_id: number | null;
  benefit_type: BenefitType;
  metric_key: string;
  metric_label: string | null;
  baseline_value: string | null;
  projected_value: string | null;
  realized_value: string | null;
  unit: string | null;
  source: string;
  formula_version: string | null;
  citation: string | null;
  as_of_date: string | null;
  realized_as_of_date: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  OptimizationOpportunity?: {
    id: number;
    title: string;
    inefficiency_type: string;
    data_source: string;
  };
}

export interface BenefitRollupMetric {
  metric_key: string;
  metric_label: string;
  unit: string | null;
  aggregation: 'sum' | 'avg';
  projected: number | null;
  realized: number | null;
  projectedCount: number;
  realizedCount: number;
}

export interface BenefitRollupType {
  benefit_type: BenefitType;
  recordCount: number;
  realizedCount: number;
  opportunityCount: number;
  metrics: BenefitRollupMetric[];
}

export interface BenefitRollup {
  totalRecords: number;
  totalRealized: number;
  byType: BenefitRollupType[];
}

export interface CreateBenefitPayload {
  opportunity_id: number;
  benefit_type: BenefitType;
  metric_key: string;
  metric_label?: string;
  baseline_value?: number | null;
  projected_value?: number | null;
  unit?: string;
  citation?: string;
  as_of_date?: string;
  notes?: string;
}

export const opportunityBenefitsAPI = {
  getBenefits: async (
    projectId: number,
    filters?: { benefit_type?: BenefitType; opportunity_id?: number }
  ): Promise<OpportunityBenefit[]> => {
    const params = new URLSearchParams();
    if (filters?.benefit_type) params.set('benefit_type', filters.benefit_type);
    if (filters?.opportunity_id) params.set('opportunity_id', String(filters.opportunity_id));
    const qs = params.toString();
    const response = await api.get(`/api/opportunity-benefits/${projectId}${qs ? `?${qs}` : ''}`);
    return response.data.benefits;
  },

  getRollup: async (projectId: number): Promise<BenefitRollup> => {
    const response = await api.get(`/api/opportunity-benefits/${projectId}/rollup`);
    return response.data.rollup;
  },

  createBenefit: async (projectId: number, payload: CreateBenefitPayload): Promise<OpportunityBenefit> => {
    const response = await api.post(`/api/opportunity-benefits/${projectId}`, payload);
    return response.data.benefit;
  },

  recordRealized: async (
    projectId: number,
    benefitId: number,
    payload: { realized_value: number | null; realized_as_of_date?: string; notes?: string }
  ): Promise<OpportunityBenefit> => {
    const response = await api.patch(
      `/api/opportunity-benefits/${projectId}/${benefitId}/realized`,
      payload
    );
    return response.data.benefit;
  },

  syncBenefits: async (projectId: number): Promise<{ synced: number; opportunities: number }> => {
    const response = await api.post(`/api/opportunity-benefits/${projectId}/sync`);
    return response.data;
  }
};

export const BENEFIT_TYPE_META: Record<BenefitType, { label: string; color: string; bg: string; border: string }> = {
  cost_savings: { label: 'Cost Savings', color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
  growth_capacity: { label: 'Growth / Capacity', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
  efficiency: { label: 'Efficiency', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  risk_control_automation: { label: 'Risk / Automated Controls', color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200' }
};

export function formatBenefitValue(value: number | string | null, unit: string | null): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '—';
  switch (unit) {
    case 'usd_per_year':
      return num >= 1_000_000 ? `$${(num / 1_000_000).toFixed(1)}M/yr` : `$${Math.round(num / 1000)}K/yr`;
    case 'pct':
      return `${num}%`;
    case 'hours_per_year':
      return `${Math.round(num).toLocaleString()} hrs/yr`;
    case 'count':
      return `${Math.round(num)}`;
    case 'per_week':
      return `${num}/wk`;
    default:
      return `${num}`;
  }
}
